import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { IPickerOption } from './IPickerOption';
import type { IPickerProps } from './IPickerProps';
import { filterOptions } from './filterOptions';
import { nextEnabledIndex } from './nextEnabledIndex';
import { useFloating } from './useFloating';

/**
 * Стан і клавіатура пікера (прототип pickerVM): закритий — ↓ / Enter / пробіл відкриває з виділеним поточним значенням;
 * відкритий — ↑ ↓ Home End рухають виділення, Enter вибирає, Tab закриває (Esc — useFloating); втрата фокуса закриває за 140 мс.
 */
export function usePicker({ value, options, onChange, searchable, query, onQueryChange, readOnly, disabled, onOpenChange, size = 'md' }: IPickerProps) {
  const [open, setOpen] = useState(false);
  const [localQuery, setLocalQuery] = useState('');
  const [hi, setHi] = useState(-1);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const listId = useId();
  const remote = onQueryChange !== undefined;
  const q = remote ? (query ?? '') : localQuery;
  const shown = useMemo(() => (remote ? options : filterOptions(options, q)), [remote, options, q]);
  const showSearch = size !== 'key' && (searchable ?? (remote || options.length > 7));
  const setQuery = useCallback((s: string) => { if (remote) onQueryChange?.(s); else setLocalQuery(s); setHi(0); }, [remote, onQueryChange]);
  const notify = useRef(onOpenChange);
  useEffect(() => { notify.current = onOpenChange; }, [onOpenChange]);
  const close = useCallback(() => { setOpen(false); setHi(-1); if (remote) onQueryChange?.(''); else setLocalQuery(''); notify.current?.(false); }, [remote, onQueryChange]);
  const show = useCallback(() => {
    if (readOnly || disabled) return;
    setOpen(true); notify.current?.(true);
    setHi(Math.max(0, options.findIndex((o) => o.value === value)));
  }, [readOnly, disabled, options, value]);
  const pick = useCallback((o: IPickerOption | undefined) => {
    if (!o || o.disabled) return;
    onChange(o.value); close(); trigger.current?.focus();
  }, [onChange, close]);
  const estimate = Math.min(300, shown.length * 34 + (showSearch ? 48 : 12));
  const pos = useFloating(open, trigger, panel, close, { estimate, minWidth: size === 'inline' ? 160 : 0 });
  const disabledAt = useMemo(() => shown.map((o) => Boolean(o.disabled)), [shown]);
  const onKeyDown = (e: KeyboardEvent) => {
    const k = e.key;
    if (!open) { if (k === 'ArrowDown' || k === 'ArrowUp' || k === 'Enter' || (k === ' ' && e.target === trigger.current)) { e.preventDefault(); show(); } return; }
    if (k === 'ArrowDown' || k === 'ArrowUp') { e.preventDefault(); setHi((h) => nextEnabledIndex(disabledAt, h, k === 'ArrowDown' ? 1 : -1)); }
    else if (k === 'Home' || k === 'End') { e.preventDefault(); setHi(nextEnabledIndex(disabledAt, 0, k === 'Home' ? 'first' : 'last')); }
    else if (k === 'Enter' || (k === ' ' && e.target === trigger.current)) { e.preventDefault(); pick(shown[hi]); }
    else if (k === 'Tab') close();
  };
  const openRef = useRef(open);
  useEffect(() => { openRef.current = open; }, [open]);
  const onBlur = () => setTimeout(() => {
    const a = document.activeElement;
    if (!openRef.current || (a && (trigger.current?.contains(a) || panel.current?.contains(a)))) return;
    close();
  }, 140);
  const toggle = () => (open ? close() : show());
  return { open, q, setQuery, shown, showSearch, hi, setHi, trigger, panel, listId, pos, pick, toggle, onKeyDown, onBlur };
}
